/**
 * Normalizes a Brazilian phone number for storage and WhatsApp URLs.
 * Always returns digits only, with "55" country code prefix.
 *
 * Examples:
 *   "(81) 99999-9999"  → "5581999999999"
 *   "81999999999"      → "5581999999999"
 *   "5581999999999"    → "5581999999999"  (idempotent)
 *   "+5581999999999"   → "5581999999999"
 */
export function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  // Already has country code 55
  if (digits.startsWith("55") && digits.length >= 12) return digits;
  return "55" + digits;
}

/**
 * Normalizes a complete Brazilian phone number for WhatsApp.
 * Accepts 10/11 local digits or 12/13 digits prefixed with country code 55.
 * Returns an empty string when the DDD or subscriber number is incomplete.
 */
export function normalizeBrazilianPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  const local = digits.startsWith("55") && (digits.length === 12 || digits.length === 13)
    ? digits.slice(2)
    : digits;

  if (local.length !== 10 && local.length !== 11) return "";

  const ddd = Number(local.slice(0, 2));
  if (ddd < 11 || ddd > 99) return "";

  const subscriber = local.slice(2);
  const validLandline = subscriber.length === 8 && /^[2-5]/.test(subscriber);
  const validMobile = subscriber.length === 9 && subscriber.startsWith("9");
  if (!validLandline && !validMobile) return "";

  return `55${local}`;
}

export function isValidBrazilianPhone(raw: string): boolean {
  return Boolean(normalizeBrazilianPhone(raw));
}

/**
 * Formats a stored phone number for display (removes country code for Brazilian numbers).
 * "5581999999999" → "(81) 99999-9999"
 * "5581999999999" → "(81) 9999-9999"
 */
export function displayPhone(stored: string): string {
  const digits = stored.replace(/\D/g, "");
  // Strip leading 55 country code if present
  const local = digits.startsWith("55") && digits.length >= 12
    ? digits.slice(2)
    : digits;
  const d = local.slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}
